import { Values } from "../context";
import { Payload } from "../../../api/drafts";
import { DATE_FORMAT } from "@constants";
import { format, isAfter } from "date-fns";

function getRequestHeader(
  type: Values["type"],
  justification: Values["justification"] | undefined,
  target: Values["target"] | undefined,
  date: Values["date"] | undefined
): Payload["requestHeader"] {
  function getRequestType(
    value: Values["type"]
  ): Payload["requestHeader"]["requestType"] {
    switch (value) {
      case "new":
        return 1;
      case "modify":
        return 2;
      case "revoke":
        return 3;
      default:
        throw new Error("Missing request type");
    }
  }

  if (!target) {
    throw new Error("Missing request target");
  }

  return {
    requestType: getRequestType(type),
    justification: justification,
    draftRequestForself: target === "SELF",
    effectiveDate: date ? format(date, DATE_FORMAT) : undefined,
  };
}

function getUserList(
  users: Values["users"] = [],
  revoke_type: Values["revoke_type"]
): Payload["userList"] {
  return users.map((user) => ({
    emailAddress: user.email,
    networkId: user.id,
    name: user.name,
    revokeType: revoke_type || "",
    startDate: user.start_date ? format(user.start_date, DATE_FORMAT) : "",
    isAdvance: user.start_date ? isAfter(user.start_date, Date.now()) : false,
  }));
}

function getAppList(systems: Values["systems"] = []): Payload["appList"] {
  const appList: Payload["appList"] = [];

  for (const system of systems) {
    for (const access of system.accesses) {
      appList.push({
        keyId: undefined,
        appId: system.application.id,
        appName: system.application.name,
        appFormId: undefined,
        host: system.application.host,
        appType: system.application.type,
        environmentVal: undefined,
        environment: access.environment ? access.environment.name : "N/A",
        environmentId: access.environment ? access.environment.id : Number.NaN,
        role: access.role.name,
        roleId: access.role.id,
        deviceTypeAppId: undefined,
        infraAppId: access.infrastructureApplication?.id || Number.NaN,
        details: access.accessDetails,
        device: access.subCategoryName,
      });
    }
  }

  return appList;
}

function getInfraDetails(
  systems: Values["systems"] = []
): Payload["infraDetails"] {
  const infraDetails: Payload["infraDetails"] = [];

  for (const system of systems) {
    if (system.application.manual) {
      for (const access of system.accesses) {
        if (access.infrastructureApplication) {
          infraDetails.push({
            infraKeyId: undefined,
            environmentVal: undefined,
            infraAppId: access.infrastructureApplication.id,
            environment: access.environment.name,
            role: access.role.name,
            subCategoryName: undefined,
            details: access.accessDetails,
            device: undefined,
            deviceTypeId: undefined,
            infraApp: access.infrastructureApplication.id,
            environmentId: access.environment.id,
            roleId: access.role.id,
          });
        }
      }
    }
  }

  return infraDetails;
}

function getAppValues(systems: Values["systems"] = []): Payload["appValues"] {
  return systems.map((system) => ({
    id: system.application.id,
    appName: system.application.name,
    host: system.application.host,
    type: system.application.type,
    accessDetailNote: system.application.accessDetailNote,
    additionalForm: system.application.additionalForm,
    categoryId: system.application.categoryId,
    systemTypeId: system.application.systemTypeId,
    singleRequest: system.application.singleRequest,
    uamNote: system.application.uamNote,
    formTemplate: system.application.formTemplate,
    formTemplate2: system.application.formTemplate2,
    isRevokeNeededForm: system.application.revokeNeededForm,
    mobileAttestation: system.application.mobileAttestation,
  }));
}

function getRoleValues(systems: Values["systems"] = []): Payload["roleValues"] {
  const roleValues: Payload["roleValues"] = [];

  for (const { accesses } of systems) {
    for (const { role } of accesses) {
      if (!roleValues.find((roleValue) => roleValue.id === role.id)) {
        roleValues.push({
          id: role.id,
          roleName: role.name,
        });
      }
    }
  }

  return roleValues;
}

function getEnvironmentValues(
  systems: Values["systems"] = []
): Payload["environmentValues"] {
  const environmentValues: Payload["environmentValues"] = [];

  for (const { accesses } of systems) {
    for (const { environment } of accesses) {
      if (!environment) {
        environmentValues.push({
          id: Number.NaN,
          environmentName: "N/A",
        });
      } else {
        if (
          !environmentValues.find(
            (environmentValue) => environmentValue.id === environment.id
          )
        ) {
          environmentValues.push({
            id: environment.id,
            environmentName: environment.name,
          });
        }
      }
    }
  }

  return environmentValues;
}

function getInfraAppValues(
  systems: Values["systems"] = []
): Payload["infraAppValues"] {
  const infraAppValues: Payload["infraAppValues"] = [];

  for (const { accesses } of systems) {
    for (const { infrastructureApplication } of accesses) {
      if (
        infrastructureApplication &&
        !infraAppValues.find(
          (infraAppValue) => infraAppValue.id === infrastructureApplication.id
        )
      ) {
        infraAppValues.push({
          id: infrastructureApplication.id,
          appName: infrastructureApplication.name,
        });
      }
    }
  }

  return infraAppValues;
}

export function getDraftPayloadValuesFromRequestContext(
  values: Partial<Values>
): Payload {
  return {
    requestHeader: getRequestHeader(
      values.type,
      values.justification,
      values.target,
      values.date
    ),
    userList: getUserList(values.users, values.revoke_type),
    appList: getAppList(values.systems),
    infraDetails: getInfraDetails(values.systems),
    appValues: getAppValues(values.systems),
    roleValues: getRoleValues(values.systems),
    environmentValues: getEnvironmentValues(values.systems),
    infraAppValues: getInfraAppValues(values.systems),
    id:
      values.draft_key === undefined || values.draft_key === ""
        ? undefined
        : parseInt(values.draft_key),
  };
}
